import React, { useState } from 'react';
import {useNavigate, useLocation, Navigate, Link} from 'react-router-dom';
import { login } from '../services/user-service';
import ErrorToast from '../components/ErrorToast';
import SuccessToast from '../components/SuccessToast';
import LoginRequest from "../models/LoginRequest";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { useAuth } from '../context/AuthContext';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorToast, setErrorToast] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [successMessage, setSuccessMessage] = useState(location.state?.message || '');
    const [loading, setLoading] = useState(false);
    const { createSession, isAuthenticated } = useAuth();

    const validate = (): boolean => {
        const errs: Record<string,string> = {};

        if (email.length > 100) {
            errs.email = 'Email must be 100 characters or fewer';
        }
        if (password.length > 15) {
            errs.password = 'Password must be 15 characters or fewer';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorToast('');
        setSuccessMessage('');
        setLoading(true);

        if(!validate()) {
            setLoading(false);
            return
        }

        try {
            const loginRequest: LoginRequest = {
                username: email,
                password: password
            }

            const response = await login(loginRequest);
            if (response.status === 200 && response.data) {
                createSession({
                        idToken: response.data.idToken,
                        accessToken: response.data.accessToken,
                        refreshToken: response.data.refreshToken,
                        user: response.data.user
                    })
                navigate('/dashboard');
            } else if (response.status === 400) {
                setErrorToast('Incorrect username or password');
            } else if (response.status === 409) {
                navigate('/confirm', {
                    state: {
                        error: 'Email has not been confirmed. Please confirm before logging in.'
                    }
                });
            } else {
                console.error(response.data)
                setErrorToast('An unexpected error occurred. Please try again later.');
            }
        } catch (error: any) {
            console.error(error.response)
            setErrorToast('An unexpected error occurred. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        isAuthenticated ? <Navigate to="/dashboard" /> :
        <div className="flex flex-col min-h-screen bg-gray-50" >
            <NavBar />
            <div className="flex-grow mt-16">
                <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded shadow ">
                    <form
                        onSubmit={handleSubmit}
                        className="w-full max-w-md space-y-6 rounded-xl">
                        <h2 className="text-center text-2xl font-semibold text-crumbblue">Log In</h2>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:border-crumbblue focus:outline-none"
                                data-testid="email-input"
                            />
                            {errors.email && (
                                <div data-testid="email-error" className="text-red-600 text-sm mt-1">
                                    {errors.email}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Password</label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:border-crumbblue focus:outline-none"
                                data-testid="password-input"
                            />
                            {errors.password && (
                                <div data-testid="password-error" className="text-red-600 text-sm mt-1">
                                    {errors.password}
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full rounded-md bg-crumbblue p-2 font-semibold text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {loading ? 'Logging in...' : 'Log In'}
                        </button>
                        <div className="text-center space-y-2">
                            <Link
                                to="/signup"
                                className="block text-crumbblue hover:underline text-sm font-medium"
                            >
                                Create account
                            </Link>
                            <Link
                                to="/forgot-password"
                                className="block text-crumbblue hover:underline text-sm font-medium"
                            >
                                Forgot password?
                            </Link>
                        </div>
                    </form>
                    <SuccessToast data-testid='success-message' message={successMessage} clearSuccess={() => setSuccessMessage('')} />
                    <ErrorToast data-testid='error-message' message={errorToast} clearError={() => setErrorToast('')} />
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default LoginPage;